import { useEffect, useLayoutEffect, useRef } from "react"
import { turnstileSiteKey } from "../lib/botProtection"

// Renders the Cloudflare Turnstile widget when a site-key is configured;
// renders nothing otherwise (graceful degradation — see the Bot Protection
// playbook). The Turnstile script is loaded globally from the Rails layout
// (`shared/_turnstile.html.erb`); this component just hosts the mount point
// and calls `turnstile.render` programmatically so we can wire the
// onSuccess / onExpired callbacks back into React state.
//
// Callers spread `useBotProtection().turnstileProps` into it, which carries
// the four props below.
//
// `onToken(token)` fires with the token string on a successful challenge,
// or null when there is no usable token (mount, reset, expiry, error). The
// parent keeps it and passes it on submit.
//
// `resetSignal` is a counter the parent increments after a server-side
// rejection. A Turnstile token is single-use and expires after a few minutes,
// so without an explicit reset a retry resubmits the same dead token and fails
// forever -- which is what made a Turnstile false positive unrecoverable on
// the primary conversion action. Issue #166.
//
// `onPending(bool)` brackets every stretch with no token: from mount until the
// first callback (#339), from a reset until the fresh callback (#166), and from
// an expiry until Turnstile's automatic refresh lands. Submitting in one sends
// an empty `cf-turnstile-response`, which the server refuses, so the parent
// holds a press made then and sends it when pending ends. Any callback ends
// it, an error included: the server then decides, and a QA run carrying the
// bypass (#320) gets through. With no site key nothing is ever pending, so dev
// and CI never wait.
//
// `onGone()` fires when the widget unmounts, so the parent can drop a held
// press instead of sending it from a form that has closed.

// The safety valve. A slow or blocked script, or a challenge that turns
// interactive and sits unanswered, must not hold the form forever. After this
// long the wait ends without a token; the server still has the last word.
const PENDING_TIMEOUT_MS = 10_000

export default function Turnstile({ onToken, onPending, onGone, resetSignal = 0 }) {
  const siteKey = turnstileSiteKey()
  const ref = useRef(null)
  const widgetId = useRef(null)
  const waitTimer = useRef(null)
  // Callbacks live in refs so the mount effect doesn't depend on their
  // identity: an inline `onToken={t => ...}` would otherwise remove and
  // re-render the widget on every parent render, and the reset effect (keyed
  // on resetSignal alone) would call a stale closure.
  const onTokenRef = useRef(onToken)
  const onPendingRef = useRef(onPending)
  const onGoneRef = useRef(onGone)
  useEffect(() => {
    onTokenRef.current = onToken
    onPendingRef.current = onPending
    onGoneRef.current = onGone
  })

  // Both read refs only, so any render's copy is safe to call.
  function startWaiting() {
    clearTimeout(waitTimer.current)
    onPendingRef.current?.(true)
    waitTimer.current = setTimeout(() => {
      waitTimer.current = null
      onPendingRef.current?.(false)
    }, PENDING_TIMEOUT_MS)
  }

  function stopWaiting() {
    clearTimeout(waitTimer.current)
    waitTimer.current = null
    onPendingRef.current?.(false)
  }

  // The first token. A layout effect, so the submit button reads "Checking
  // your browser…" from the first paint rather than flashing its label first.
  // It also clears any token a previous widget left (one already spent on a
  // create, say): this widget is the one that answers now.
  useLayoutEffect(() => {
    if (!siteKey) return
    onTokenRef.current?.(null)
    startWaiting()
    return () => {
      clearTimeout(waitTimer.current)
      waitTimer.current = null
      onGoneRef.current?.()
    }
  }, [siteKey])

  useEffect(() => {
    if (!siteKey || !ref.current) return
    let cancelled = false

    // A fresh token or an error ends the wait. Ending it on error too is
    // deliberate: the submit must not stay held just because Turnstile is
    // having a bad day.
    function settle(token) {
      stopWaiting()
      onTokenRef.current?.(token)
    }

    function mount() {
      if (cancelled || !window.turnstile || !ref.current) return
      // The check starts now, so the wait runs from here. A script that loaded
      // after the first wait timed out would otherwise leave a gap with no
      // token and nothing holding the press.
      startWaiting()
      widgetId.current = window.turnstile.render(ref.current, {
        sitekey: siteKey,
        theme: "light",
        size: "flexible",
        callback: (token) => settle(token),
        // Turnstile refreshes an expired token by itself (refresh-expired
        // defaults to "auto"), so this is a wait, not a failure.
        "expired-callback": () => {
          onTokenRef.current?.(null)
          startWaiting()
        },
        "error-callback": () => settle(null)
      })
    }

    function teardown() {
      cancelled = true
      if (widgetId.current && window.turnstile) {
        try { window.turnstile.remove(widgetId.current) } catch (e) { /* noop */ }
      }
      widgetId.current = null
    }

    if (window.turnstile) {
      mount()
    } else {
      // Script still loading; poll briefly for window.turnstile.
      const id = setInterval(() => {
        if (window.turnstile) {
          clearInterval(id)
          mount()
        }
      }, 100)
      return () => {
        clearInterval(id)
        teardown()
      }
    }

    return teardown
  }, [siteKey])

  // Skips the mount run: `widgetId` is null then anyway, and a fresh widget
  // needs no reset.
  useEffect(() => {
    if (!resetSignal || !widgetId.current || !window.turnstile) return
    try {
      window.turnstile.reset(widgetId.current)
    } catch (e) {
      /* widget already gone; the next mount issues a fresh one */
      return
    }
    onTokenRef.current?.(null)
    startWaiting()
  }, [resetSignal])

  if (!siteKey) return null
  return <div ref={ref} className="mt-3" />
}
